import * as Y from 'yjs';

// WeakMap of Y types → their cached JS snapshot. Shared across all hook
// instances and renders. Entries auto-evict when the Y type is garbage
// collected (i.e. removed from its parent and no other ref holds it).
//
// The cache is the perf-critical optimisation: `yToJS(yElements)` over a
// 100-table doc would otherwise recursively allocate ~10,000 JS objects on
// every observeDeep tick. With this cache, only the subtree actually
// affected by an event is re-converted; unchanged children retain their
// JS object references so React.memo / useMemo see stable inputs.
const snapshotCache = new WeakMap();

export function yToJS(value) {
    if (value instanceof Y.Map) {
        const cached = snapshotCache.get(value);
        if (cached !== undefined) return cached;
        const out = {};
        value.forEach((v, k) => { out[k] = yToJS(v); });
        snapshotCache.set(value, out);
        return out;
    }
    if (value instanceof Y.Array) {
        const cached = snapshotCache.get(value);
        if (cached !== undefined) return cached;
        const out = value.toArray().map(yToJS);
        snapshotCache.set(value, out);
        return out;
    }
    return value;
}

// Invalidate the cached snapshot for `root` and every Y type along the
// given path (the path emitted by Y events on observeDeep).
//
// observeDeep paths are arrays of keys/indices from the observed root down
// to the immediate parent of the mutated value. Walking the path drops
// every cached ancestor between root and the mutation, which is exactly
// what's needed: parents include their children's snapshots, so a child's
// mutation invalidates the chain up to root.
export function invalidateAlong(root, path) {
    if (!root) return;
    snapshotCache.delete(root);
    let cur = root;
    for (const segment of path) {
        if (cur instanceof Y.Map) cur = cur.get(segment);
        else if (cur instanceof Y.Array) cur = cur.get(segment);
        else break;
        if (!cur) break;
        snapshotCache.delete(cur);
    }
}
